import { create } from 'zustand';

/**
 * Auth state. The web app keeps the access token in **memory only** — the
 * refresh token is held in an httpOnly cookie set by the API and never
 * touches JS. On cold-start the app calls /auth/refresh once; success
 * rehydrates `user` via /users/me, failure routes to /welcome.
 *
 * No persist middleware here on purpose: persisting tokens to localStorage
 * would defeat the httpOnly-cookie threat model.
 */

export interface AuthUser {
  id: string;
  email: string;
  name: string | null;
  birthDate: string | null;
  birthTime: string | null;
  birthPlace: string | null;
  gender: 'male' | 'female' | null;
  timezone: string | null;
  useDeviceTimezone: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Two endpoints fill this and they do NOT agree:
 *   - POST /auth/login  → { isPremium, plan, endDate, source }  (server-derived)
 *   - GET  /subscription → { id, plan, status, startDate, endDate, productId }
 *
 * `refreshSession()` uses the second on every cold start, so `isPremium` is
 * genuinely absent much of the time. Marked optional so nothing can read it as
 * a guaranteed boolean — use `isPremium()` from lib/paywall-gate instead.
 */
export interface SubscriptionInfo {
  isPremium?: boolean;
  plan: string;
  endDate: string | null;
  /** Only present on the raw GET /subscription row. */
  status?: string;
}

interface AuthState {
  status: 'unknown' | 'authenticated' | 'unauthenticated';
  user: AuthUser | null;
  accessToken: string | null;
  subscription: SubscriptionInfo | null;

  setAuthenticated: (args: {
    user: AuthUser;
    accessToken: string;
    subscription?: SubscriptionInfo | null;
  }) => void;
  setAccessToken: (token: string) => void;
  setUser: (user: AuthUser) => void;
  setSubscription: (sub: SubscriptionInfo | null) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  status: 'unknown',
  user: null,
  accessToken: null,
  subscription: null,

  setAuthenticated: ({ user, accessToken, subscription }) =>
    set({
      status: 'authenticated',
      user,
      accessToken,
      subscription: subscription ?? null,
    }),
  setAccessToken: (accessToken) => set({ accessToken }),
  setUser: (user) => set({ user }),
  setSubscription: (subscription) => set({ subscription }),
  clear: () =>
    set({
      status: 'unauthenticated',
      user: null,
      accessToken: null,
      subscription: null,
    }),
}));

/** Synchronous getter for the axios interceptor — must not use the React hook. */
export function getAccessToken(): string | null {
  return useAuthStore.getState().accessToken;
}
